import { useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useReaderMode } from '../../hooks/useReaderMode';

interface Props {
  /** Stable identifier for this surface. Should be unique per mountable
   *  surface (e.g. `chat-${sessionId}`, `code-${filePath}`, `plan-${planId}`). */
  id: string;
  /** Optional class forwarded to the wrapper element. */
  className?: string;
  children: ComponentChildren;
}

/**
 * Wrap any text-bearing surface to make it Reader-Mode aware. Registers the
 * container ref with the global ReaderModeProvider so the overlay can resolve
 * `surfaceId → element` for anchoring + text extraction.
 *
 * Must be used in conjunction with `<ReaderToggle surfaceId={...}>` and
 * `data-reader-block-id` attributes on individual readable blocks
 * (chat-message bubbles, plan steps, paragraphs).
 */
export function ReaderSurface({ id, className, children }: Props) {
  const reader = useReaderMode();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return reader.registerSurface(id, el);
  }, [id, reader]);

  return (
    <div ref={ref} class={`reader-surface ${className || ''}`.trim()} data-reader-surface={id}>
      {children}
    </div>
  );
}
